import { ref, computed } from '@vue/composition-api'

/**
 * Composable for spin wheel logic
 * Handles rotation calculation, prize selection, and animation state
 */
export function useSpinWheel(options = {}) {
  const { minSpins = 5, maxSpins = 8, duration = 4000 } = options

  // State
  const isSpinning = ref(false)
  const currentRotation = ref(0)
  const targetRotation = ref(0)

  /**
   * Calculate the angle for a specific prize index
   * The wheel segments start at the TOP (12 o'clock position)
   * Segment 0 spans from top going clockwise
   * @param {number} prizeIndex - Index of the prize
   * @param {number} totalPrizes - Total number of prizes
   * @returns {number} - Angle in degrees where prize center is located (CSS rotation perspective)
   */
  function getPrizeAngle(prizeIndex, totalPrizes) {
    const segmentAngle = 360 / totalPrizes
    // For CSS rotation: segment 0 center is at segmentAngle/2 from 0
    // When wheel is not rotated, segment 0 is at top (spanning from 0 to segmentAngle)
    // Its center is at segmentAngle/2 from the starting edge
    return prizeIndex * segmentAngle + segmentAngle / 2
  }

  /**
   * Calculate final rotation to land on target prize
   * @param {number} prizeIndex - Index of prize to land on
   * @param {number} totalPrizes - Total number of prizes
   * @returns {number} - Total rotation in degrees
   */
  function calculateTargetRotation(prizeIndex, totalPrizes) {
    const segmentAngle = 360 / totalPrizes

    // Random number of full spins
    const spins = Math.floor(minSpins + Math.random() * (maxSpins - minSpins))

    // The segment's center offset from its starting edge
    const halfSegment = segmentAngle / 2

    // To land on segment N, we need final rotation where:
    // - Segment N's center aligns with pointer at top (0°)
    // - Segment N center is at: N * segmentAngle + halfSegment (from rotation 0)
    // - We need to rotate backwards by that amount, or forwards by (360 - that)
    //
    // Simpler approach: final rotation mod 360 should put segment N at top
    // rotation = -(N * segmentAngle + halfSegment) mod 360
    // rotation = 360 - (N * segmentAngle + halfSegment) for positive rotation

    const targetAngle = 360 - (prizeIndex * segmentAngle + halfSegment)

    // Normalize to positive
    const normalizedTarget = ((targetAngle % 360) + 360) % 360

    // Total rotation = full spins + target angle
    const totalRotation = spins * 360 + normalizedTarget

    return totalRotation
  }

  /**
   * Select a prize based on probability weights
   * @param {Array} prizes - Array of prize objects with probability property
   * @returns {number} - Index of selected prize
   */
  function selectPrizeByProbability(prizes) {
    const totalProbability = prizes.reduce(
      (sum, p) => sum + (p.probability || 1),
      0
    )
    let random = Math.random() * totalProbability

    for (let i = 0; i < prizes.length; i += 1) {
      random -= prizes[i].probability || 1
      if (random <= 0) {
        return i
      }
    }
    return prizes.length - 1
  }

  /**
   * Start the spin animation
   * @param {Object} params - Spin parameters
   * @param {Array} params.prizes - Array of prizes
   * @param {number} [params.targetIndex] - Optional specific prize index to land on
   * @returns {Promise<Object>} - Resolves with winning prize
   */
  async function spin({ prizes, targetIndex = null }) {
    if (isSpinning.value) {
      return null
    }

    isSpinning.value = true

    // Determine winning prize index
    const winningIndex =
      targetIndex !== null ? targetIndex : selectPrizeByProbability(prizes)

    // Calculate rotation
    targetRotation.value = calculateTargetRotation(winningIndex, prizes.length)

    // Wait for animation to complete
    return new Promise(resolve => {
      setTimeout(() => {
        currentRotation.value = targetRotation.value
        isSpinning.value = false
        resolve({
          index: winningIndex,
          prize: prizes[winningIndex]
        })
      }, duration)
    })
  }

  /**
   * Reset the wheel to initial state
   */
  function reset() {
    currentRotation.value = 0
    targetRotation.value = 0
    isSpinning.value = false
  }

  // Computed rotation style
  const rotationStyle = computed(() => ({
    transform: `rotate(${targetRotation.value}deg)`,
    transition: isSpinning.value
      ? `transform ${duration}ms cubic-bezier(0.17, 0.67, 0.12, 0.99)`
      : 'none'
  }))

  return {
    // State
    isSpinning,
    currentRotation,
    targetRotation,
    rotationStyle,

    // Methods
    spin,
    reset,
    getPrizeAngle,
    selectPrizeByProbability
  }
}

/**
 * Generate SVG path for a wheel segment
 * @param {number} index - Segment index
 * @param {number} total - Total segments
 * @param {number} radius - Wheel radius
 * @param {number} innerRadius - Inner radius (for donut style, default 0)
 * @param {number} center - Center point of the wheel (defaults to radius for backwards compatibility)
 * @returns {string} - SVG path data
 */
export function generateSegmentPath(
  index,
  total,
  radius,
  innerRadius = 0,
  center = null
) {
  const cx = center !== null ? center : radius
  const cy = center !== null ? center : radius

  const angle = (2 * Math.PI) / total
  const startAngle = index * angle - Math.PI / 2 // Start from top
  const endAngle = startAngle + angle

  const x1 = cx + radius * Math.cos(startAngle)
  const y1 = cy + radius * Math.sin(startAngle)
  const x2 = cx + radius * Math.cos(endAngle)
  const y2 = cy + radius * Math.sin(endAngle)

  const largeArc = angle > Math.PI ? 1 : 0

  if (innerRadius > 0) {
    // Donut style
    const ix1 = cx + innerRadius * Math.cos(startAngle)
    const iy1 = cy + innerRadius * Math.sin(startAngle)
    const ix2 = cx + innerRadius * Math.cos(endAngle)
    const iy2 = cy + innerRadius * Math.sin(endAngle)

    return `M ${ix1} ${iy1} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2} L ${ix2} ${iy2} A ${innerRadius} ${innerRadius} 0 ${largeArc} 0 ${ix1} ${iy1} Z`
  }

  // Pie style (full segment from center)
  return `M ${cx} ${cy} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2} Z`
}

/**
 * Calculate text position for a segment label
 * @param {number} index - Segment index
 * @param {number} total - Total segments
 * @param {number} radius - Wheel radius
 * @param {number} textRadius - Distance from center for text (default 65% of radius)
 * @param {number} center - Center point of the wheel (defaults to radius for backwards compatibility)
 * @returns {Object} - { x, y, rotation } for text positioning
 */
export function getSegmentTextPosition(
  index,
  total,
  radius,
  textRadius = null,
  center = null
) {
  const cx = center !== null ? center : radius
  const cy = center !== null ? center : radius

  const angle = (2 * Math.PI) / total
  const midAngle = index * angle + angle / 2 - Math.PI / 2
  const r = textRadius || radius * 0.65

  // Convert mid angle to degrees
  let rotation = (midAngle * 180) / Math.PI + 90

  // Normalize rotation to 0-360 range
  while (rotation < 0) rotation += 360
  while (rotation >= 360) rotation -= 360

  // If text would be upside down (pointing down-ish), flip it 180 degrees
  // This keeps text readable from the outside
  if (rotation > 90 && rotation < 270) {
    rotation += 180
  }

  return {
    x: cx + r * Math.cos(midAngle),
    y: cy + r * Math.sin(midAngle),
    rotation
  }
}
